/* eslint-disable unused-imports/no-unused-imports  */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prefer-const */
import { SadFaceIcon } from '@helloself/hello-self-ui';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { orderBy, sortBy } from 'lodash';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import * as FilesApi from '../../../../api/files/files';
import * as MessagingApi from '../../../../api/messaging/messaging';
import {
    ConversationAttachment,
    ConversationMessage,
    ConversationMessageWithSendingStatus,
    CreateConversationRequest,
    GetAllConversationStatusesRequest,
    GetConversationMessagesRequest,
    GetConversationStatusRequest,
    MarkConversationMessagesAsReadRequest,
} from '../../../../api/messaging/types';
import { AttachmentFile } from '../../../../api/messaging/types';
import { initialState } from '../initialState';
import { MessagingSlice } from '../types';
import { updatePendingMessageWithUploadProgress } from './actions';

export const createNewConversation = createAsyncThunk(
    'messaging/createNewConversation',
    async (args: CreateConversationRequest) => {
        const statusForCreatedConvo = await MessagingApi.createNewConversation(args);

        return {
            status: statusForCreatedConvo,
        };
    }
);

export const getConversationStatus = createAsyncThunk(
    'messaging/getConversationStatus',
    async (args: GetConversationStatusRequest) => {
        const status = await MessagingApi.getConversationStatus(args);

        if (status) {
            return {
                status: status,
            };
        } else {
            throw new Error('No conversation found');
        }
    }
);

export const sendMessage = createAsyncThunk(
    'messaging/sendMessage',
    async (
        args: {
            conversationId: string;
            currentExpertId: string;
            otherParticipantId: string;
            messageBody?: string;
            replyToMessageId?: string;
            attachments?: AttachmentFile[];
            lastSeqId: number;
        },
        thunkAPI
    ) => {
        const uploadedAttachments: ConversationAttachment[] = [];

        const uploadAttachment = async (attachment: AttachmentFile, attachmentIndex: number) => {
            const response = await FilesApi.uploadAttachment(
                args.currentExpertId,
                attachment.fileType,
                attachment.fileName,
                attachment.fileBlob,
                {
                    entityType: 'Conversation',
                    entityId: args.conversationId,
                },
                (progress) => {
                    if (progress) {
                        thunkAPI.dispatch(
                            updatePendingMessageWithUploadProgress({
                                conversationId: args.conversationId,
                                tempMessageId: thunkAPI.requestId,
                                attachmentIndex: attachmentIndex,
                                progress,
                            })
                        );
                    }
                }
            );

            uploadedAttachments.push({
                fileUuid: response.fileUUID,
                fileDisplayName: response.destinationFilename,
                fileSize: attachment.fileSize,
                mimeType: attachment.fileType,
            });
        };

        // Visually, we only support one attachment at the moment
        // But we may want to allow more than one in future

        if (args.attachments) {
            const requests: Promise<void>[] = [];

            args.attachments.forEach((attachment, attachmentIndex) => {
                requests.push(uploadAttachment(attachment, attachmentIndex));
            });

            await Promise.all(requests);
        }

        const status = await MessagingApi.sendMessage({
            conversationId: args.conversationId,
            messageData: {
                messageBody: args.messageBody || '',
                attachments: uploadedAttachments,
                replyToMessageId: args.replyToMessageId || null,
            },
        });

        return {
            status: status,
        };
    }
);

export const populateInitialConversationMessages = createAsyncThunk(
    'messaging/populateInitialConversationMessages',
    async (args: { conversationId: string; latestMessageSeqId: number }) => {
        let seqId = args.latestMessageSeqId;
        let totalMessages: ConversationMessage[] = [];
        while (seqId > 0) {
            seqId = seqId - 1;
            const response = await MessagingApi.getConversationMessages({
                conversationId: args.conversationId,
                seqId: seqId,
            });
            totalMessages.push(...response.messages);
        }

        totalMessages = sortBy(totalMessages, 'seqId', 'asc');

        // remove duplicates
        totalMessages = totalMessages.filter(
            (val, index, self) => index === self.findIndex((msg) => msg.messageId === val.messageId)
        );

        return {
            conversationId: args.conversationId,
            latestMessageSeqId: args.latestMessageSeqId,
            messages: totalMessages,
        };

        // return {
        //     ...getDummyConversationMessages('96b00ae5-0487-4121-ad3d-698ed687c475'),
        //     conversationId: args.conversationId,
        // };
    }
);

export const populateConversationMessagesSinceLastFetch = createAsyncThunk(
    'messaging/populateConversationMessagesSinceLastFetch',
    async (args: GetConversationMessagesRequest) => {
        let pullAgain = true;
        const totalNewMessages: ConversationMessage[] = [];
        let latestSeqIdPulled = args.seqId;
        let latestMessageSeqId = args.seqId;

        while (pullAgain === true) {
            const messageStatus = await MessagingApi.getConversationMessages({
                conversationId: args.conversationId,
                seqId: latestSeqIdPulled,
            });

            totalNewMessages.push(...messageStatus.messages);

            latestMessageSeqId = messageStatus.latestMessageSeqId;

            if (messageStatus.messages.length > 0) {
                const latestMessageInBatch = messageStatus.messages[messageStatus.messages.length - 1];
                latestSeqIdPulled = latestMessageInBatch.seqId;
            }

            if (latestSeqIdPulled < latestMessageSeqId) {
                pullAgain = true;
            } else {
                pullAgain = false;
            }
        }

        return {
            conversationId: args.conversationId,
            messages: totalNewMessages,
            latestMessageSeqId: latestMessageSeqId,
        };

        // return {
        //     ...getDummyConversationMessages('96b00ae5-0487-4121-ad3d-698ed687c475'),
        //     conversationId: args.conversationId,
        // };
    }
);

export const populateAllConversations = createAsyncThunk('messaging/populateAllConversations', async () => {
    const statuses = await MessagingApi.getAllConversationStatuses();
    return {
        statuses: statuses,
    };

    // return {
    //     statuses: getDummyConversationStatuses('96b00ae5-0487-4121-ad3d-698ed687c475'),
    // };
});

export const populateAllConversationsAfterTimestamp = createAsyncThunk(
    'messaging/populateAllConversations',
    async (args: GetAllConversationStatusesRequest) => {
        const statuses = await MessagingApi.getAllConversationStatusesAfterTimestamp(args);
        return {
            statuses: statuses,
        };
        // return {
        //     statuses: getDummyConversationStatuses('96b00ae5-0487-4121-ad3d-698ed687c475'),
        // };
    }
);

export const markMessagesAsRead = createAsyncThunk(
    'messaging/markMessagesAsRead',
    async (args: MarkConversationMessagesAsReadRequest) => {
        const status = await MessagingApi.markConversationMessagesAsRead(args);

        return {
            status: status,
        };
    }
);

const reducer = (builder: ActionReducerMapBuilder<MessagingSlice>) => {
    builder.addCase(createNewConversation.fulfilled, (state, action) => {
        const conversationStatus = action.payload.status;
        state.conversations.conversationStatuses.push(conversationStatus);
        state.isInsertingNewConversation = false;
    });
    builder.addCase(createNewConversation.pending, (state, action) => {
        state.isInsertingNewConversation = true;
    });
    builder.addCase(createNewConversation.rejected, (state, action) => {
        console.error(`Error creating new  conversation: `, action.error);
        state.isInsertingNewConversation = false;
    });
    builder.addCase(getConversationStatus.fulfilled, (state, action) => {
        const conversationStatus = action.payload.status;
        const index = state.conversations.conversationStatuses.findIndex(
            (convo) => convo.conversationId === conversationStatus.conversationId
        );
        if (index > -1) {
            state.conversations.conversationStatuses[index] = conversationStatus;
        } else {
            state.conversations.conversationStatuses.push(conversationStatus);
        }
    });
    builder.addCase(getConversationStatus.rejected, (state, action) => {
        console.error(`Error getting status for conversation ${action.meta.arg.conversationId}: `, action.error);
    });
    builder.addCase(sendMessage.pending, (state, action) => {
        const pendingMessage: ConversationMessageWithSendingStatus = {
            tempId: action.meta.requestId,
            seqId: action.meta.arg.lastSeqId + 1,
            messageBody: action.meta.arg.messageBody,
            replyToMessageId: action.meta.arg.replyToMessageId,
            createdAt: new Date().toISOString(),
            createdBy: {
                userType: 'expert',
                userId: action.meta.arg.currentExpertId,
            },
            attachments: (action.meta.arg.attachments || []).map((attachmentFile) => ({
                fileUuid: uuidv4(),
                fileDisplayName: attachmentFile.fileName,
                fileSize: attachmentFile.fileSize,
                mimeType: attachmentFile.fileType,
                action: 'uploading',
                actionProgressPercentage: 0,
            })),
        };

        const conversationId = action.meta.arg.conversationId;

        if (state.pendingMessages[conversationId]) {
            state.pendingMessages[conversationId].push(pendingMessage);
        } else {
            state.pendingMessages[conversationId] = [pendingMessage];
        }
        // return {
        //     ...state,
        //     pendingMessages: {
        //         ...state.pendingMessages,
        //         [conversationId] : [
        //             ...state.pendingMessages[conversationId],
        //             pendingMessage,
        //         ]
        //     }
        // }
    });
    builder.addCase(sendMessage.fulfilled, (state, action) => {
        const conversationStatus = action.payload.status;
        const index = state.conversations.conversationStatuses.findIndex(
            (convo) => convo.conversationId === conversationStatus.conversationId
        );
        const newConversationStatuses = [...state.conversations.conversationStatuses];
        if (index > -1) {
            newConversationStatuses[index] = conversationStatus;
        } else {
            newConversationStatuses.push(conversationStatus);
        }

        // remove this message from the pending messages array
        // if (Object.keys(state.pendingMessages).includes(conversationStatus.conversationId)) {
        //     let pendingMessages = [...state.pendingMessages[conversationStatus.conversationId]];
        //     const pendingMessageIndex = pendingMessages.findIndex((msg) => msg.tempId === action.meta.requestId);
        //     if (pendingMessageIndex > -1) {
        //         pendingMessages.splice(pendingMessageIndex, 1);
        //         state.pendingMessages = {
        //             ...state.pendingMessages,
        //             [conversationStatus.conversationId]: pendingMessages,
        //         };
        //     }
        // }

        state.conversations.conversationStatuses = newConversationStatuses;

        // Note - this doesn't bring down the new message
        // Messages need to be refreshed using PopulateConversationMessages
    });
    builder.addCase(sendMessage.rejected, (state, action) => {
        console.error(
            `Error sending expert-to-expert message for conversation ${action.meta.arg.conversationId}: `,
            action.error
        );
        toast.error('There was a problem sending that message. Please try again in a few minutes', {
            icon: SadFaceIcon,
            position: 'top-center',
        });

        // find the pending message object for this message
        const conversationId = action.meta.arg.conversationId;
        const pendingMessageIndex = state.pendingMessages[conversationId]?.findIndex(
            (msg) => msg.tempId === action.meta.requestId
        );
        if (pendingMessageIndex > -1) {
            const pendingMessage = state.pendingMessages[conversationId][pendingMessageIndex];

            let existingFailedMessages = state.failedToSendMessages[conversationId];
            if (!existingFailedMessages) {
                existingFailedMessages = [];
            }
            // add it to failedToSendMessages
            const failedMessages = [...existingFailedMessages, pendingMessage];
            state.failedToSendMessages = {
                ...state.failedToSendMessages,
                [conversationId]: failedMessages,
            };

            // remove it from pending
            const pendingMessages = state.pendingMessages[conversationId];
            pendingMessages.splice(pendingMessageIndex, 1);

            state.pendingMessages = {
                ...state.pendingMessages,
                [conversationId]: pendingMessages,
            };
        }
    });
    builder.addCase(populateInitialConversationMessages.fulfilled, (state, action) => {
        // this will contain the latest messages only
        // organised by seqId
        const conversationId = action.payload.conversationId;
        const messages = action.payload.messages;

        const latestMessageSeqId = action.payload.latestMessageSeqId;
        let latestMessage = undefined;
        if (messages.length >= latestMessageSeqId) {
            // we have a complete list of messages
            // which is where we should be
            latestMessage = messages[messages.length - 1];
        } else {
            // we haven't finished loading messages yet
            console.error(
                `populate initial  conversation messages did not find all messages for conversation ${action.payload.conversationId}`
            );
        }

        state.messages = {
            ...state.messages,
            [conversationId]: {
                latestMessageSeqId: action.payload.latestMessageSeqId,
                messages: messages,
            },
        };
        state.mostRecentMessages = {
            ...state.mostRecentMessages,
            [conversationId]: latestMessage,
        };

        // removed from conversations that failed to load messages
        // if it is in there
        const index = state.didErrorWhileLoadingMessages.findIndex((id) => id === conversationId);
        if (index > -1) {
            state.didErrorWhileLoadingMessages.splice(index, 1);
        }

        if (!state.hasInitialisedMessages.includes(conversationId)) {
            state.hasInitialisedMessages.push(conversationId);
        }
    });
    builder.addCase(populateInitialConversationMessages.rejected, (state, action) => {
        console.error(`populate initial  conversation messages rejected: `, action.error);
        // add to conversations that failed to load messages
        // if it isn't in there
        if (!state.didErrorWhileLoadingMessages.some((id) => id === action.meta.arg.conversationId)) {
            state.didErrorWhileLoadingMessages.push(action.meta.arg.conversationId);
        }
    });
    builder.addCase(populateConversationMessagesSinceLastFetch.fulfilled, (state, action) => {
        // this will contain up to 10 messages
        // organised by seqId
        const conversationId = action.payload.conversationId;
        const messages = action.payload.messages;

        let existingMessagesForConversation: ConversationMessage[] = [];
        if (state.messages[conversationId]) {
            existingMessagesForConversation = [...state.messages[conversationId].messages];
        }

        messages.forEach((message) => {
            if (!existingMessagesForConversation.some((msg) => msg.messageId === message.messageId)) {
                existingMessagesForConversation.push(message);
            }
        });

        const updatedMessages = orderBy(existingMessagesForConversation, 'seqId', 'asc');

        const latestMessageSeqId = action.payload.latestMessageSeqId;
        let latestMessage = state.mostRecentMessages[conversationId];
        if (updatedMessages.length === latestMessageSeqId) {
            // we have a complete list of messages
            latestMessage = updatedMessages[latestMessageSeqId - 1];
        } else {
            // we haven't finished loading messages yet
        }

        const statusIndex = state.conversations.conversationStatuses.findIndex(
            (convo) => convo.conversationId === action.payload.conversationId
        );
        if (statusIndex >= 0) {
            const status = state.conversations.conversationStatuses[statusIndex];
            if (status) {
                status.latestMessageSeqId = action.payload.latestMessageSeqId;
            }
        } else {
            console.error(
                `did not find status when fetching messages for conversation ${action.payload.conversationId}`
            );
        }
        state.messages = {
            ...state.messages,
            [conversationId]: {
                latestMessageSeqId: action.payload.latestMessageSeqId,
                messages: updatedMessages,
            },
        };
        state.mostRecentMessages = {
            ...state.mostRecentMessages,
            [conversationId]: latestMessage,
        };
    });
    builder.addCase(populateConversationMessagesSinceLastFetch.rejected, (state, action) => {
        console.error(`populate convo messages since last fetch rejected: `, action.error);
    });
    builder.addCase(populateAllConversations.fulfilled, (state, action) => {
        if (action.payload.statuses) {
            state.conversations = action.payload.statuses;
            state.isLoadingConversations = false;
            state.didErrorWhileLoadingConversations = false;
        }
    });
    builder.addCase(populateAllConversations.pending, (state, action) => {
        state.isLoadingConversations = true;
    });
    builder.addCase(populateAllConversations.rejected, (state, action) => {
        state.isLoadingConversations = false;
        state.didErrorWhileLoadingConversations = true;
        console.error(`populate all expert-to-expert conversations rejected: `, action.error);
    });
    builder.addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const conversationStatus = action.payload.status;

        const index = state.conversations.conversationStatuses.findIndex(
            (convo) => convo.conversationId === conversationStatus.conversationId
        );
        const newConversationStatuses = [...state.conversations.conversationStatuses];
        if (index > -1) {
            newConversationStatuses[index] = conversationStatus;
        } else {
            newConversationStatuses.push(conversationStatus);
        }

        state.conversations = {
            ...state.conversations,
            conversationStatuses: newConversationStatuses,
        };

        let unreadMessages = false;
        state.conversations.conversationStatuses.forEach((status) => {
            if (status.latestMessageSeqId > status.latestReadSeqId) {
                unreadMessages = true;
                return;
            }
        });
        state.haveUnreadMessages = unreadMessages;
    });
    builder.addCase(markMessagesAsRead.rejected, (state, action) => {
        console.error(`mark expert-to-expert messages read rejected: `, action.error);
    });
    builder.addCase(updatePendingMessageWithUploadProgress, (state, action) => {
        let pendingMessages: ConversationMessageWithSendingStatus[] = [];
        if (Object.keys(state.pendingMessages).includes(action.payload.conversationId)) {
            pendingMessages = state.pendingMessages[action.payload.conversationId];
        }

        const pendingMessageIndex = pendingMessages.findIndex(
            (message) => action.payload.tempMessageId === message.tempId
        );
        if (pendingMessageIndex > -1) {
            const pendingMessage = pendingMessages[pendingMessageIndex];
            const attachments = pendingMessage.attachments.map((attachment, attachmentIndex) => {
                const newAttachment = attachment;
                if (attachmentIndex === action.payload.attachmentIndex) {
                    newAttachment.action = 'uploading';
                    newAttachment.actionProgressPercentage = Math.floor(
                        (action.payload.progress.loaded / action.payload.progress.total) * 100
                    );
                }
                return newAttachment;
            });
            pendingMessage.attachments = attachments;
            pendingMessages[pendingMessageIndex] = pendingMessage;
        }

        state.pendingMessages[action.payload.conversationId] = pendingMessages;
    });
};

export default reducer;
